import React from 'react';
import { MapPinned, PenTool, Hammer, Zap, CheckCircle } from 'lucide-react';

const ProcessStep = ({ number, icon: Icon, title, description, items, isLast }) => {
  return (
    <div className="relative">
      <div className="flex gap-6">
        {/* Timeline */}
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-emerald-700 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg relative z-10">
            {number}
          </div>
          {!isLast && (
            <div className="w-0.5 h-full bg-emerald-200 mt-4" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 pb-12">
          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 leading-relaxed">{description}</p>
              </div>
            </div>
            <ul className="mt-6 space-y-3 ml-16">
              {items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Process = () => {
  const steps = [
    {
      icon: MapPinned,
      title: 'Site Selection & Acquisition',
      description: 'Identifying optimal locations with maximum solar irradiation and grid connectivity.',
      items: [
        'Solar resource assessment and land surveys',
        'Grid connectivity feasibility studies',
        'Environmental and social impact assessments',
        'Land acquisition and legal clearances'
      ]
    },
    {
      icon: PenTool,
      title: 'Engineering & Design',
      description: 'Detailed project design ensuring optimal performance and compliance.',
      items: [
        'Electrical and civil engineering design',
        'Equipment selection and procurement planning',
        'Grid integration studies and approvals',
        'Safety and compliance documentation'
      ]
    },
    {
      icon: Hammer,
      title: 'Construction & Installation',
      description: 'Professional execution with strict quality control and safety standards.',
      items: [
        'Civil works and foundation installation',
        'Solar panel mounting and electrical works',
        'Inverter and transformer installation',
        'Quality testing and commissioning'
      ]
    },
    {
      icon: Zap,
      title: 'Operation & Maintenance',
      description: 'Long-term asset management ensuring consistent performance.',
      items: [
        '24/7 monitoring and performance tracking',
        'Preventive and corrective maintenance',
        'Energy generation reporting',
        'Continuous optimization and upgrades'
      ]
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A systematic approach from concept to commissioning, ensuring quality at every stage
          </p>
        </div>

        {/* Process Steps */}
        <div className="space-y-0">
          {steps.map((step, index) => (
            <ProcessStep
              key={index}
              number={index + 1}
              {...step}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
